import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Box,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { getCapturedImageDisplayUrl } from '../services/s3/captureUrlService';
import { listUnknownFaces, promoteToSOI } from '../services/api/unknownFacesService';
import UnknownFaceDetailsDialog from '../appcomponents/unknownfaces/UnknownFaceDetailsDialog';

/**
 * UnknownFacesList - Table view of unknown faces with SOI promotion actions
 */
export default function UnknownFacesList() {
  const [unknownFaces, setUnknownFaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('UNKNOWN');
  const [displayedCount, setDisplayedCount] = useState(100);
  const [promoting, setPromoting] = useState(null); // ID of face being promoted
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedFace, setSelectedFace] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [faceToPromote, setFaceToPromote] = useState(null);
  const [promotionReason, setPromotionReason] = useState('');

  const loaderRef = useRef(null);
  const tableContainerRef = useRef(null);

  // Load unknown faces
  useEffect(() => {
    loadUnknownFaces();
  }, [statusFilter]);

  const loadUnknownFaces = async () => {
    try {
      setLoading(true);
      const faces = await listUnknownFaces({ status: statusFilter });
      setUnknownFaces(faces);
    } catch (error) {
      console.error('[UnknownFacesList] Error loading unknown faces:', error);
      setSnackbar({ open: true, message: 'Failed to load unknown faces', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = () => {
    setActiveSearch(searchInput);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Filter unknown faces based on active search
  const filteredFaces = useMemo(() => {
    if (!activeSearch.trim()) {
      return unknownFaces;
    }

    const criteria = activeSearch.split(',').map(c => c.trim().toLowerCase()).filter(c => c);
    if (criteria.length === 0) {
      return unknownFaces;
    }

    return unknownFaces.filter(face => {
      return criteria.every(criterion => {
        return (
          face.unknown_face_id?.toLowerCase().includes(criterion) ||
          face.status?.toLowerCase().includes(criterion)
        );
      });
    });
  }, [unknownFaces, activeSearch]);

  // Reset displayed count when filter changes
  useEffect(() => {
    setDisplayedCount(100);
  }, [unknownFaces, activeSearch, statusFilter]);

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && displayedCount < filteredFaces.length) {
          const newCount = Math.min(displayedCount + 50, filteredFaces.length);
          setDisplayedCount(newCount);
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [displayedCount, filteredFaces.length]);

  const displayedFaces = filteredFaces.slice(0, displayedCount);

  // Promote to SOI - open dialog
  const handlePromoteToSOI = (face, e) => {
    e.stopPropagation();
    setFaceToPromote(face);
    setPromotionReason('');
    setPromoteDialogOpen(true);
  };

  // Confirm promotion to SOI
  const confirmPromoteToSOI = async () => {
    if (!promotionReason.trim()) {
      setSnackbar({ open: true, message: 'Please enter a promotion reason', severity: 'error' });
      return;
    }

    try {
      setPromoting(faceToPromote.unknown_face_id);
      setPromoteDialogOpen(false);
      await promoteToSOI(faceToPromote.unknown_face_id, promotionReason.trim());
      setSnackbar({ open: true, message: 'Successfully promoted to SOI', severity: 'success' });
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSnackbar({ open: false, message: '', severity: 'success' }), 3000);
      // Reload to get updated status
      loadUnknownFaces();
    } catch (error) {
      console.error('[UnknownFacesList] Error promoting to SOI:', error);
      setSnackbar({ open: true, message: 'Failed to promote to SOI', severity: 'error' });
      // Auto-hide error message after 4 seconds
      setTimeout(() => setSnackbar({ open: false, message: '', severity: 'success' }), 4000);
    } finally {
      setPromoting(null);
      setFaceToPromote(null);
      setPromotionReason('');
    }
  };

  // Get image URL from first sighting
  function getImageUrl(face) {
    if (!face.sightings || face.sightings.length === 0) return null;
    const s3Key = face.sightings[0].capture_s3_key;
    return getCapturedImageDisplayUrl(s3Key);
  }

  function formatDate(isoString) {
    if (!isoString) return '—';
    const date = new Date(isoString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-GB', { month: 'short' });
    const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${day} ${month} ${time}`;
  }

  function getSightingChipColor(count) {
    if (count >= 10) return 'error';
    if (count >= 5) return 'warning';
    return 'default';
  }

  function getStatusChipColor(status) {
    switch(status) {
      case 'PROMOTED_TO_SOI': return 'warning';
      case 'IDENTIFIED': return 'success';
      default: return 'default';
    }
  }

  function getStatusLabel(status) {
    switch(status) {
      case 'PROMOTED_TO_SOI': return 'SOI';
      case 'IDENTIFIED': return 'Identified';
      case 'UNKNOWN': return 'Unknown';
      default: return status || 'Unknown';
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1 }}>
      {/* Search and Filter Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="UNKNOWN">Unknown</MenuItem>
              <MenuItem value="PROMOTED_TO_SOI">Subject of Interest</MenuItem>
              <MenuItem value="IDENTIFIED">Identified</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            placeholder="Search by ID..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchInput('');
                      setActiveSearch('');
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSearch}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              borderRadius: 1
            }}
          >
            <SearchIcon />
          </IconButton>
        </Box>
      </Paper>

      <TableContainer
        ref={tableContainerRef}
        component={Paper}
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          flex: 1,
          overflow: 'auto'
        }}
      >
        <Table size="small" sx={{ '& .MuiTableCell-root': { py: 1, fontSize: '0.8125rem' } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 50 }}>
                Img
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary' }}>
                Unknown Face ID
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 90 }}>
                Sightings
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 70 }}>
                Days
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 110 }}>
                First Seen
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 110 }}>
                Last Seen
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 100 }}>
                Status
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 120 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedFaces.map((face) => (
              <TableRow
                key={face.unknown_face_id}
                hover
                onClick={() => {
                  setSelectedFace(face);
                  setDetailsOpen(true);
                }}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  '&:last-child td': { borderBottom: 0 }
                }}
              >
                {/* Image Thumbnail */}
                <TableCell sx={{ p: 0.5 }}>
                  {getImageUrl(face) ? (
                    <Box
                      component="img"
                      src={getImageUrl(face)}
                      alt={face.unknown_face_id}
                      sx={{
                        width: 40,
                        height: 40,
                        objectFit: 'cover',
                        borderRadius: 0.5,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'grey.100',
                        borderRadius: 0.5,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>?</Typography>
                    </Box>
                  )}
                </TableCell>

                {/* Unknown Face ID */}
                <TableCell>
                  <Typography
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 600,
                      fontSize: '0.8125rem',
                      color: 'secondary.main'
                    }}
                  >
                    {face.unknown_face_id}
                  </Typography>
                </TableCell>

                {/* Sighting Count */}
                <TableCell align="center">
                  <Chip
                    label={face.sighting_count || 0}
                    size="small"
                    color={getSightingChipColor(face.sighting_count)}
                    sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 600 }}
                  />
                </TableCell>

                {/* Unique Days */}
                <TableCell align="center">
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                    {face.unique_days?.length || 0}
                  </Typography>
                </TableCell>

                {/* First Seen */}
                <TableCell>
                  <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                    {formatDate(face.first_seen)}
                  </Typography>
                </TableCell>

                {/* Last Seen */}
                <TableCell>
                  <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                    {formatDate(face.last_seen)}
                  </Typography>
                </TableCell>

                {/* Status */}
                <TableCell align="center">
                  <Chip
                    label={getStatusLabel(face.status)}
                    size="small"
                    color={getStatusChipColor(face.status)}
                    sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 600 }}
                  />
                </TableCell>

                {/* Actions */}
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end' }}>
                    {face.status === 'UNKNOWN' && (
                      <Tooltip title="Flag as Subject of Interest" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => handlePromoteToSOI(face, e)}
                          disabled={promoting === face.unknown_face_id}
                          sx={{
                            '&:hover': { bgcolor: 'warning.50', color: 'warning.main' }
                          }}
                        >
                          {promoting === face.unknown_face_id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <PersonSearchIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="View Details" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFace(face);
                          setDetailsOpen(true);
                        }}
                        sx={{
                          '&:hover': { bgcolor: 'info.50', color: 'info.main' }
                        }}
                      >
                        <VisibilityIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Infinite scroll loader */}
        {displayedCount < filteredFaces.length && (
          <Box
            ref={loaderRef}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 2
            }}
          >
            <CircularProgress size={24} />
            <Typography sx={{ ml: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
              Loading more... ({displayedCount} of {filteredFaces.length})
            </Typography>
          </Box>
        )}

        {/* Empty state */}
        {filteredFaces.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              px: 2
            }}
          >
            <PersonSearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No unknown faces found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeSearch ? `No matches for: ${activeSearch}` : `No ${statusFilter.toLowerCase()} faces`}
            </Typography>
          </Box>
        )}
      </TableContainer>

      {/* Status bar */}
      <Paper
        elevation={0}
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          px: 2,
          py: 1
        }}
      >
        <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
          Showing {displayedCount} of {filteredFaces.length}
          {activeSearch && ` (${unknownFaces.length} total)`}
          {displayedCount < filteredFaces.length && ' - scroll down to load more'}
        </Typography>
      </Paper>

      {/* Snackbar */}
      {snackbar.open && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999
          }}
        >
          <Paper
            elevation={6}
            sx={{
              px: 3,
              py: 1.5,
              bgcolor: snackbar.severity === 'error' ? 'error.main' : 'success.main',
              color: 'white'
            }}
          >
            <Typography>{snackbar.message}</Typography>
          </Paper>
        </Box>
      )}

      {/* Details Dialog */}
      <UnknownFaceDetailsDialog
        open={detailsOpen}
        face={selectedFace}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedFace(null);
        }}
        onRefresh={loadUnknownFaces}
      />

      {/* Promote to SOI Dialog */}
      <Dialog
        open={promoteDialogOpen}
        onClose={() => setPromoteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Promote to Subject of Interest</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Why is this person of interest? (This will be visible in the SOI record)
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Promotion Reason"
            value={promotionReason}
            onChange={(e) => setPromotionReason(e.target.value)}
            placeholder="e.g., Frequent visitor, suspicious behavior, VIP, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmPromoteToSOI}
            variant="contained"
            color="warning"
            disabled={!promotionReason.trim()}
          >
            Promote to SOI
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
